import {CoreGraphNodeId} from '../../../../../@polygonjs/polygonjs/src/core/graph/CoreGraph';
import {ref, Ref} from '@vue/composition-api';
import {AVAILABLE_LINK_INDEX_VALUES, LinkIndex} from '../../../components/panels/common/LinkIndex';
import {BaseNodeType} from '../../../../../@polygonjs/polygonjs/src/engine/nodes/_Base';
import {StoreController} from '../StoreController';
import {ArrayUtils} from '../../../../../@polygonjs/polygonjs/src/core/ArrayUtils';

interface CurrentNodeIdHistoryByLinkIndex {
	1: CoreGraphNodeId[];
	2: CoreGraphNodeId[];
	3: CoreGraphNodeId[];
	4: CoreGraphNodeId[];
	5: CoreGraphNodeId[];
	6: CoreGraphNodeId[];
	7: CoreGraphNodeId[];
	8: CoreGraphNodeId[];
}
interface CurrentNodeIdHistoryByLinkIndexJSON {
	1: string[];
	2: string[];
	3: string[];
	4: string[];
	5: string[];
	6: string[];
	7: string[];
	8: string[];
}
interface CurrentNodeIdHistoryIndexByLinkIndex {
	1: Ref<number>;
	2: Ref<number>;
	3: Ref<number>;
	4: Ref<number>;
	5: Ref<number>;
	6: Ref<number>;
	7: Ref<number>;
	8: Ref<number>;
}
interface CurrentNodeIdHistoryIndexByLinkIndexJSON {
	1: number;
	2: number;
	3: number;
	4: number;
	5: number;
	6: number;
	7: number;
	8: number;
}

export interface NavigationHistoryJSON {
	nodePaths: CurrentNodeIdHistoryByLinkIndexJSON;
	index: CurrentNodeIdHistoryIndexByLinkIndexJSON;
}

const DEFAULT_INDEX = -1;
export class NavigationHistoryStoreControllerClass {
	private static _instance: NavigationHistoryStoreControllerClass;
	static instance() {
		return (this._instance = this._instance || new NavigationHistoryStoreControllerClass());
	}

	private constructor() {}

	// navigation cannot be using nodeIds, as when a node is deleted, and recreated via undo, that stops working
	// it cannot use paths either, as nodes can be renamed
	// using both id and paths does not seem very reliable either
	// ideally, I would use the missing references system, which is also used for nodepath params.
	// but for now, I'll use ids, and when we goForward, goBackward, request node lists, or save toJSON,
	// or when nodes are added/deleted/renamed in the store
	// then the history gets a sanity check done
	private _nodeId!: Ref<CurrentNodeIdHistoryByLinkIndex>;
	private _index!: CurrentNodeIdHistoryIndexByLinkIndex;
	init() {
		this._nodeId = ref({
			1: [],
			2: [],
			3: [],
			4: [],
			5: [],
			6: [],
			7: [],
			8: [],
		});

		this._index = {
			1: ref(DEFAULT_INDEX),
			2: ref(DEFAULT_INDEX),
			3: ref(DEFAULT_INDEX),
			4: ref(DEFAULT_INDEX),
			5: ref(DEFAULT_INDEX),
			6: ref(DEFAULT_INDEX),
			7: ref(DEFAULT_INDEX),
			8: ref(DEFAULT_INDEX),
		};
	}
	reset() {
		for (let linkIndex of AVAILABLE_LINK_INDEX_VALUES) {
			this._nodeId.value[linkIndex].splice(0, this._nodeId.value[linkIndex].length);
			this._index[linkIndex].value = DEFAULT_INDEX;
		}
	}
	private _performSanityCheck() {
		for (let linkIndex of AVAILABLE_LINK_INDEX_VALUES) {
			const index = this._index[linkIndex];
			let i = 0;
			for (let nodeId of this._nodeId.value[linkIndex]) {
				const node = StoreController.engine.node(nodeId);
				if (!node) {
					this._nodeId.value[linkIndex].splice(i, 1);
					if (i <= index.value) {
						index.value -= 1;
					}
				}
				i++;
			}
		}
	}

	// getters
	currentNodeHistory(linkIndex: LinkIndex): CoreGraphNodeId[] {
		return this._nodeId.value[linkIndex];
	}
	currentNodeHistoryIndex(linkIndex: LinkIndex): number {
		return this._index[linkIndex].value;
	}

	goBackwardAllowed(linkIndex: LinkIndex) {
		const index = this._index[linkIndex].value;
		const history = this._nodeId.value[linkIndex];
		return index > 0 && history.length > 0;
	}
	goForwardAllowed(linkIndex: LinkIndex) {
		const index = this._index[linkIndex].value;
		const history = this._nodeId.value[linkIndex];
		return index < history.length - 1;
	}
	toJSON(): NavigationHistoryJSON | undefined {
		try {
			this._performSanityCheck();
			const indexJSON: CurrentNodeIdHistoryIndexByLinkIndexJSON = {
				1: this._index[1].value,
				2: this._index[2].value,
				3: this._index[3].value,
				4: this._index[4].value,
				5: this._index[5].value,
				6: this._index[6].value,
				7: this._index[7].value,
				8: this._index[8].value,
			};
			const nodeName = (nodeId: CoreGraphNodeId) => {
				return StoreController.engine.node(nodeId)!.path();
			};
			const nodePathsJSON: CurrentNodeIdHistoryByLinkIndexJSON = {
				1: this._nodeId.value[1].map(nodeName),
				2: this._nodeId.value[2].map(nodeName),
				3: this._nodeId.value[3].map(nodeName),
				4: this._nodeId.value[4].map(nodeName),
				5: this._nodeId.value[5].map(nodeName),
				6: this._nodeId.value[6].map(nodeName),
				7: this._nodeId.value[7].map(nodeName),
				8: this._nodeId.value[8].map(nodeName),
			};

			return {
				nodePaths: nodePathsJSON,
				index: indexJSON,
			};
		} catch (err) {
			console.log(err);
			console.warn('navigation history will not be saved');
		}
	}

	// mutations
	fromJSON(json: NavigationHistoryJSON) {
		let linkIndices = Object.keys(json.nodePaths);
		for (let i of linkIndices) {
			const linkIndex = parseInt(i) as LinkIndex;
			const nodePaths = json.nodePaths[linkIndex];
			for (let nodePath of nodePaths) {
				const node = StoreController.scene.node(nodePath);
				const nodeId = node?.graphNodeId();
				if (nodeId != null) {
					this._nodeId.value[linkIndex].push(nodeId);
				}
			}
		}
		linkIndices = Object.keys(json.index);
		for (let i of linkIndices) {
			const linkIndex = parseInt(i) as LinkIndex;
			const index = this._index[linkIndex];
			index.value = json.index[linkIndex];
			const currentNodeId = this._nodeId.value[linkIndex][index.value];
			if (currentNodeId != null) {
				const node = StoreController.engine.node(currentNodeId);
				if (node) {
					StoreController.editor.setCurrentNode(linkIndex, node, {pushToHistory: false});
				}
			}
		}
	}

	pushCurrentNode(linkIndex: LinkIndex, node: BaseNodeType) {
		const nodeId = node.graphNodeId();
		const index = this._index[linkIndex];
		const nodeIds = this._nodeId.value[linkIndex];
		if (nodeIds.length > index.value + 1) {
			nodeIds.splice(index.value + 1, nodeIds.length - index.value);
		}
		const lastNodeId = nodeIds[nodeIds.length - 1];
		if (lastNodeId == nodeId) {
			return;
		}
		nodeIds.push(nodeId);
		while (nodeIds.length > 100) {
			nodeIds.shift();
		}
		index.value = nodeIds.length - 1;
	}
	goBackward(linkIndex: LinkIndex) {
		this._performSanityCheck();
		if (!this.goBackwardAllowed(linkIndex)) {
			return;
		}
		this._go(linkIndex, -1);
	}
	goForward(linkIndex: LinkIndex) {
		this._performSanityCheck();
		if (!this.goForwardAllowed(linkIndex)) {
			return;
		}
		this._go(linkIndex, +1);
	}

	toggleDisplayBackwardNodeList(event: MouseEvent, linkIndex: LinkIndex, element: HTMLElement) {
		this._performSanityCheck();
		this._toggleDisplayNodeList(
			event,
			linkIndex,
			ArrayUtils.shallowClone(this.backwardNodeIds(linkIndex)).reverse(),
			element
		);
	}
	toggleDisplayForwardNodeList(event: MouseEvent, linkIndex: LinkIndex, element: HTMLElement) {
		this._performSanityCheck();
		this._toggleDisplayNodeList(event, linkIndex, this.forwardNodeIds(linkIndex), element);
	}
	private _toggleDisplayNodeList(
		event: MouseEvent,
		linkIndex: LinkIndex,
		nodeIds: CoreGraphNodeId[],
		element: HTMLElement
	) {
		StoreController.editor.navigationNodeList.toggleDisplay({
			position: {
				x: event.pageX,
				y: event.pageY,
			},
			linkIndex: linkIndex,
			nodeIds: nodeIds,
			parentId: null,
			element: element,
		});
	}

	backwardNodeIds(linkIndex: LinkIndex) {
		return this._nodeId.value[linkIndex].slice(0, this._index[linkIndex].value);
	}
	forwardNodeIds(linkIndex: LinkIndex) {
		return this._nodeId.value[linkIndex].slice(this._index[linkIndex].value + 1);
	}
	private _go(linkIndex: LinkIndex, offset: number) {
		const index = this._index[linkIndex];
		index.value += offset;
		const history = this._nodeId.value[linkIndex];
		const nodeId = history[index.value];
		if (!nodeId) {
			return;
		}
		const node = StoreController.engine.node(nodeId);
		if (!node) {
			return;
		}
		StoreController.editor.setCurrentNode(linkIndex, node, {pushToHistory: false});
	}
}

export const NavigationHistoryStoreController = NavigationHistoryStoreControllerClass.instance();
